/**
 * gamepadInput.ts — Web Gamepad API polling + button mapping
 *
 * BETA 16.58 — Wave 3 of the mega settings push. Per Dylan: "i can play
 * with a controller. nothing put in and i dont see it in setings."
 *
 * Standard mapping (Xbox / PlayStation / generic Bluetooth controllers
 * report this layout via the "standard" button index).
 *
 * BETA 19.21 — Header doc updated to match the actual code after several
 * remap rounds (16.82 swapped A↔X; 16.99 deleted Q-tray; 19.20 removed
 * the dead LB binding). Previous doc described BETA 14-era bindings and
 * misled anyone reading the file.
 *
 *   axes[0/1] — left stick X/Y     → movement (w/a/s/d)
 *   axes[2/3] — right stick X/Y    → currently unused (auto-aim handles it)
 *
 *   button 0  — A / Cross           → menu-card confirm (level-up / shrine /
 *                                     chest cards via gamepadFocus) +
 *                                     pause-menu enter
 *   button 1  — B / Circle          → Esc (pause toggle + menu back)
 *   button 2  — X / Square          → in-game INTERACT (smash barrel,
 *                                     open chest, chick magnet, boss
 *                                     shrine, descend floor) + bulk-open
 *                                     every chest within 600wu
 *   button 3  — Y / Triangle        → context-sensitive:
 *                                       phase=playing → toggle HUD
 *                                         (chat + left stats panel)
 *                                       on choice screen → fires the
 *                                         'reroll' focus target directly
 *                                         via activateTargetById('reroll')
 *   button 4  — L1 / LB             → unused (was Q before 19.20)
 *   button 5  — R1 / RB             → unused
 *   button 6  — L2 trigger          → unused
 *   button 7  — R2 trigger          → unused
 *   button 8  — Select / Share      → unused
 *   button 9  — Start / Options     → Esc (pause toggle, alternate)
 *   button 12-15 — D-pad            → also movement (and pause-menu nav
 *                                     when paused)
 *
 * Polling: tickGamepad(state) is called once per frame from updateGame.
 * It reads navigator.getGamepads(), translates the current state into
 * key presses on state.keys, and tracks edge transitions for one-shot
 * actions (interact, pause).
 *
 * Connect/disconnect events: a singleton listener tracks whether any
 * gamepad is currently connected so the Settings UI can show
 * "Controller connected: <name>".
 */
import type { GameState } from './types';
import { handleInteract } from './engine';
import { handlePauseKeyDown } from './pauseMenu';
import { noteInputActivity } from './inputTracker';
// BETA 16.76 — Centralized UI focus / nav for menus.
import { tickGamepadFocus, getActiveScreen, getFocusedTarget, activateTargetById } from './gamepadFocus';
import { noteAPress, noteDpadPress } from './controllerAnomalies';
import { getPauseSelectedIndex } from './pauseMenu';

const STICK_DEADZONE = 0.18;
const STICK_THRESHOLD = 0.45; // beyond this on each axis we register a held key

interface GamepadEdgeState {
  // True iff the button was pressed last frame (used for rising-edge detection)
  prev: Map<number, boolean>;
  // Cardinal keys held by the stick last frame (released when no longer pushed)
  heldStickKeys: Set<string>;
  // Cardinal keys held by the dpad last frame
  heldDpadKeys: Set<string>;
}

const edge: GamepadEdgeState = {
  prev: new Map(),
  heldStickKeys: new Set(),
  heldDpadKeys: new Set(),
};

/** Public read of "is a controller currently connected?" — used by the
 *  Settings screen to show controller status. Updated by the connect/
 *  disconnect listeners installed below. */
let _connectedName: string | null = null;
let _connectedListenersInstalled = false;

export function getConnectedControllerName(): string | null {
  return _connectedName;
}

function installControllerListeners() {
  if (_connectedListenersInstalled) return;
  _connectedListenersInstalled = true;
  window.addEventListener('gamepadconnected', (e) => {
    const ge = e as GamepadEvent;
    _connectedName = ge.gamepad?.id ?? 'Controller';
    // Light haptic + console note so devs know it landed.
    try { ge.gamepad?.vibrationActuator?.playEffect?.('dual-rumble', { duration: 120, strongMagnitude: 0.3, weakMagnitude: 0.2 }); } catch { /* */ }
    // eslint-disable-next-line no-console
    console.log('[gamepad] connected:', _connectedName);
  });
  window.addEventListener('gamepaddisconnected', () => {
    _connectedName = null;
    edge.prev.clear();
    edge.heldStickKeys.clear();
    edge.heldDpadKeys.clear();
    // eslint-disable-next-line no-console
    console.log('[gamepad] disconnected');
  });
  // Some browsers (Safari) only expose pads after the user presses a
  // button; the connectedevent never fires. Poll once at install time
  // to capture any already-attached pad.
  const pads = navigator.getGamepads?.() ?? [];
  for (const p of pads) {
    if (p) {
      _connectedName = p.id || 'Controller';
      break;
    }
  }
}

/** Whether the user has the controller toggle on. The host wires this
 *  from userSettings.controllerEnabled. Default true. */
let _enabled = true;
export function setGamepadEnabled(on: boolean) {
  _enabled = on;
  if (!on) {
    // Release any currently held cardinal keys synthesized from the pad.
    edge.heldStickKeys.clear();
    edge.heldDpadKeys.clear();
  }
}

/**
 * Per-frame poll. Reads the first connected gamepad and translates its
 * state into engine input. Should be called from inside updateGame
 * before the input is consumed.
 */
export function tickGamepad(state: GameState): void {
  installControllerListeners();
  if (!_enabled) return;
  const pads = navigator.getGamepads?.() ?? [];
  let pad: Gamepad | null = null;
  for (const p of pads) {
    if (p && p.connected) { pad = p; break; }
  }
  // BETA 16.76 — When a UI focus screen is active (pause menu, level-up,
  // etc.), route gamepad input THERE only. Skip in-game button mapping
  // so A doesn't fire both menu-click AND handleInteract; B doesn't
  // fire both menu-back AND escape-pause. Movement keys are released
  // so the player doesn't walk while picking a menu option.
  if (pad && getActiveScreen()) {
    if (edge.heldStickKeys.size || edge.heldDpadKeys.size) {
      edge.heldStickKeys.forEach(k => state.keys.delete(k));
      edge.heldDpadKeys.forEach(k => state.keys.delete(k));
      edge.heldStickKeys.clear();
      edge.heldDpadKeys.clear();
    }
    // Build edge helpers BEFORE we update prev, so the focus ticker
    // sees the rising-edge transition correctly.
    const wasPressed = (i: number) => edge.prev.get(i) === true;
    const isPressed = (i: number) => !!pad!.buttons[i]?.pressed;
    // BETA 18.17 — Note A + D-pad presses for the controller anomaly
    // detector. Snapshots the pre-input state so the detector can compare
    // it against the post-input state next frame.
    const _activeScrSnap = getActiveScreen();
    const _focusedSnap = getFocusedTarget()?.id ?? null;
    if (isPressed(0) && !wasPressed(0)) noteAPress(state.phase, _focusedSnap, _activeScrSnap);
    for (const _btn of [12, 13, 14, 15] as const) {
      if (isPressed(_btn) && !wasPressed(_btn)) {
        noteDpadPress(_btn, _focusedSnap, _activeScrSnap, state.phase === 'paused' ? getPauseSelectedIndex() : null);
      }
    }
    tickGamepadFocus(pad, 1 / 60, { wasPressed, isPressed });
    // Snapshot for next frame
    for (let i = 0; i < pad.buttons.length; i++) {
      edge.prev.set(i, isPressed(i));
    }
    return;
  }
  // BETA 16.73 — Per Dylan (ROG Ally X): "it does not see my xbox
  // controls." Web Gamepad API requires a button press before any
  // gamepad becomes visible to the page (security feature). On the
  // ROG Ally X / Xbox FSE, the gamepadconnected event may have fired
  // BEFORE our listener installed (e.g. controller already paired at
  // page-load time), so _connectedName stays null even when a pad is
  // active. Now: any frame we successfully see a connected pad,
  // refresh _connectedName from pad.id. Cheap (only runs while a pad
  // is present) and self-heals across launch / reload edge cases.
  if (pad && (!_connectedName || _connectedName !== pad.id)) {
    _connectedName = pad.id || 'Controller';
  }
  if (!pad) {
    // No pad — release any pad-held keys we still have on.
    if (edge.heldStickKeys.size || edge.heldDpadKeys.size) {
      edge.heldStickKeys.forEach(k => state.keys.delete(k));
      edge.heldDpadKeys.forEach(k => state.keys.delete(k));
      edge.heldStickKeys.clear();
      edge.heldDpadKeys.clear();
    }
    return;
  }

  // BETA 16.71 — input telemetry: any frame with controller activity
  // (stick past deadzone OR any button pressed) tags this frame as
  // gamepad-active for the per-run input mix.
  let _padActive = false;
  // Stick → cardinal keys (with deadzone)
  const sx = Math.abs(pad.axes[0] ?? 0) > STICK_DEADZONE ? (pad.axes[0] ?? 0) : 0;
  const sy = Math.abs(pad.axes[1] ?? 0) > STICK_DEADZONE ? (pad.axes[1] ?? 0) : 0;
  if (sx !== 0 || sy !== 0) _padActive = true;
  const stickHeld = new Set<string>();
  if (sx <= -STICK_THRESHOLD) stickHeld.add('a');
  if (sx >=  STICK_THRESHOLD) stickHeld.add('d');
  if (sy <= -STICK_THRESHOLD) stickHeld.add('w');
  if (sy >=  STICK_THRESHOLD) stickHeld.add('s');
  // Apply diff
  stickHeld.forEach(k => { if (!edge.heldStickKeys.has(k)) state.keys.add(k); });
  edge.heldStickKeys.forEach(k => { if (!stickHeld.has(k)) state.keys.delete(k); });
  edge.heldStickKeys = stickHeld;

  // D-pad (buttons 12 up, 13 down, 14 left, 15 right) → cardinal keys
  const btn = (i: number): boolean => !!pad!.buttons[i]?.pressed;
  const dpadHeld = new Set<string>();
  if (btn(12)) dpadHeld.add('w');
  if (btn(13)) dpadHeld.add('s');
  if (btn(14)) dpadHeld.add('a');
  if (btn(15)) dpadHeld.add('d');
  dpadHeld.forEach(k => { if (!edge.heldDpadKeys.has(k)) state.keys.add(k); });
  edge.heldDpadKeys.forEach(k => { if (!dpadHeld.has(k)) state.keys.delete(k); });
  edge.heldDpadKeys = dpadHeld;

  // BETA 20.74 — hold RT (button 7, was unused) = big centered map, exactly
  // like holding TAB on keyboard (Dylan: "right trigger full screens map
  // while i play"). Level-held (not edge): the flag mirrors the trigger
  // every poll. Separate field from the keyboard hold so this per-frame
  // write can't stomp a held Tab.
  if (btn(7)) _padActive = true;
  state._bigMapPadHeld = btn(7);

  // BETA 21.11 — RB (button 5, previously unused) = DASH, the controller twin of
  // tapping Shift on keyboard. Level-held mirror into the 'dash' token; the
  // engine does its OWN rising-edge latch (player.dashHeldLastFrame) so holding
  // RB = one dash, not a repeat. Uses 'dash' (not 'shift') so it can never stomp
  // a keyboard Shift writer.
  if (btn(5)) { _padActive = true; state.keys.add('dash'); }
  else state.keys.delete('dash');

  // Edge-triggered actions (rising edge: not pressed last frame, pressed this frame)
  const wasPressed = (i: number): boolean => edge.prev.get(i) === true;
  const isPressed  = (i: number): boolean => btn(i);
  const justPressed = (i: number): boolean => isPressed(i) && !wasPressed(i);

  // BETA 18.16 — Per Dylan: "looks like everything works except for the
  // pause menu". The canvas-rendered pause menu (renderPauseMenu in
  // game/pauseMenu.ts) uses its own `selectedIndex` advanced via
  // handlePauseKeyDown('arrowup'/'arrowdown'/'enter'). It does NOT
  // register a focus screen with gamepadFocus — so tickGamepadFocus
  // can't navigate it. There's a stale `components/PauseMenu.tsx`
  // React file that DOES register 'pause_menu', but it's dead code
  // (no imports). The actual gameplay pause is canvas-side.
  // Fix: when state.phase === 'paused' AND no activeScreen is set,
  // intercept D-pad up/down + A + B and call handlePauseKeyDown
  // directly. Rising-edge only so D-pad hold doesn't skip items.
  if (state.phase === 'paused' && !getActiveScreen()) {
    if (justPressed(12)) handlePauseKeyDown(state, 'arrowup');   // D-pad up
    if (justPressed(13)) handlePauseKeyDown(state, 'arrowdown'); // D-pad down
    if (justPressed(0))  handlePauseKeyDown(state, 'enter');     // A
    // Stick rising-edge for nav (cross threshold from below)
    const _stickY = pad.axes[1] ?? 0;
    const _wasStickUp = edge.prev.get(-101) === true;
    const _wasStickDown = edge.prev.get(-102) === true;
    const _isStickUp = _stickY < -0.6;
    const _isStickDown = _stickY > 0.6;
    if (_isStickUp && !_wasStickUp) handlePauseKeyDown(state, 'arrowup');
    if (_isStickDown && !_wasStickDown) handlePauseKeyDown(state, 'arrowdown');
    edge.prev.set(-101, _isStickUp);
    edge.prev.set(-102, _isStickDown);
  }

  // BETA 16.82 — Per Dylan: "X is the action button to activate anything
  // while playing the game. why does it say press A to activate chick
  // magent?" In-game interact (smash barrel, open chest, chick magnet,
  // boss shrine, descend floor) is now X (button 2), not A (button 0).
  // X press also still opens every chest within 600wu (combined behavior).
  // A is reserved for menu-card activation via gamepadFocus (level-up /
  // shrine / chest cards) since it's the standard menu confirm on Xbox.
  // BETA 22.87 — Perk Card System v1 (Dylan: "X/action is the main interaction
  // button"). On the card pick screens X ALSO activates the focused card —
  // same path as A (gamepadFocus.activateFocused), so either button confirms.
  if (justPressed(2) && (state.phase === 'levelup' || state.phase === 'shrine_choice' || state.phase === 'chest_choice')) {
    void import('./gamepadFocus').then(m => m.activateFocused());
  }
  if (justPressed(2) && state.phase === 'playing') {
    // First: handle the player's current interact target (smash barrel,
    // chick magnet, boss shrine, single chest, descend floor, etc.)
    handleInteract(state);
    // Second: bulk-open any other chests within 600wu (the X-as-mass-open
    // behavior added in 16.76). Already-opened chests are skipped via
    // their 'gone' state, so calling this after handleInteract is safe.
    try {
      const px = state.player.x, py = state.player.y;
      const RANGE_SQ = 600 * 600;
      for (const chest of state.map.roomMap.chests) {
        if (chest.state === 'gone') continue;
        const dx = chest.x - px, dy = chest.y - py;
        if (dx * dx + dy * dy > RANGE_SQ) continue;
        const prevTarget = state.interactTarget;
        const prevTargetData = (state as unknown as { interactTargetData?: unknown }).interactTargetData;
        state.interactTarget = 'chest';
        (state as unknown as { interactTargetData?: unknown }).interactTargetData = chest;
        handleInteract(state);
        state.interactTarget = prevTarget;
        (state as unknown as { interactTargetData?: unknown }).interactTargetData = prevTargetData;
      }
    } catch { /* never crash on bulk-open */ }
  }
  // BETA 19.20 — LB (button 4) was bound to Q (active item) from
  // BETA 16.76 until 16.99 when the Q-tray was deprecated and the
  // engine's `state.keys.has('q')` consumer was removed. After that,
  // pressing LB just added 'q' to state.keys and deleted it on the
  // next tick — nothing listened. Removed. The file header doc has
  // always said "button 4 — L1 / LB → unused"; the code now matches.
  // B / Circle / View / Start → pause / resume toggle.
  // BETA 18.1 — Dylan (ROG Ally X): "i can not pause when playing on xbox
  // rog ally x when in game." Root cause: handlePauseKeyDown('escape')
  // only handles the RESUME side of the toggle (paused → playing). The
  // initial PLAYING → PAUSED transition lives in GameCanvas.tsx's KEYBOARD
  // event handler (key === 'escape'). Gamepad never dispatched a synthetic
  // keyboard event so the playing-side toggle never fired. Now: dispatch
  // a synthetic Escape KeyboardEvent so GameCanvas's handler sees the
  // exact same input path the physical Esc key takes — pause + resume
  // both work identically from gamepad.
  if (justPressed(1) || justPressed(8) /* View/Select */ || justPressed(9) /* Start/Options */) {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
    } else {
      handlePauseKeyDown(state, 'escape');
    }
  }
  // Y / Triangle → context-sensitive:
  //   In-game (phase=playing) → toggle HUD (chat + left stats panel)
  //     per Dylan: "the UI togle should just toggle the stats and chat
  //     on the left." Y is the natural HUD-toggle on Xbox controllers.
  //   Choice screens (levelup/shrine/chest) → fire the 'reroll' focus
  //     target directly. BETA 19.21 — was: state.keys.add('r'). No
  //     consumer of 'r' existed on choice screens (R only handles
  //     restart on win/gameover + resume in PauseMenu), so the press
  //     fired into the void. Now invokes the focus screen's 'reroll'
  //     target onActivate directly via gamepadFocus.activateTargetById.
  if (justPressed(3)) {
    if (state.phase === 'playing') {
      state.hudHidden = !state.hudHidden;
    } else {
      activateTargetById('reroll');
    }
  }

  // Snapshot current button state for next frame's edge detection.
  for (let i = 0; i < pad.buttons.length; i++) {
    if (isPressed(i)) _padActive = true;
    edge.prev.set(i, isPressed(i));
  }

  // Also tag dpad press as gamepad-active.
  if (dpadHeld.size > 0) _padActive = true;
  if (_padActive) noteInputActivity('gamepad');
}

/**
 * BETA 16.76 — Global gamepad poller that runs even when the game isn't
 * active. Lets the home menu / character select / settings panels
 * receive controller input. Mount once at app boot.
 */
const _menuEdge = new Map<number, boolean>();
let _menuTickerInstalled = false;
export function installMenuGamepadTicker(): void {
  if (_menuTickerInstalled) return;
  if (typeof window === 'undefined') return;
  _menuTickerInstalled = true;
  installControllerListeners();
  setInterval(() => {
    try {
      const pads = navigator.getGamepads?.() ?? [];
      let pad: Gamepad | null = null;
      for (const p of pads) { if (p && p.connected) { pad = p; break; } }
      if (!pad) return;
      if (!_connectedName || _connectedName !== pad.id) {
        _connectedName = pad.id || 'Controller';
      }
      const activeScreen = getActiveScreen();
      if (!activeScreen) return;
      const wasPressed = (i: number) => _menuEdge.get(i) === true;
      const isPressed = (i: number) => !!pad!.buttons[i]?.pressed;
      // BETA 17.05 — Per Dylan: "controller settings need to be flawless
      // and working properly for every and any situation." For React DOM
      // overlays (settings_overlay / quests_overlay / leaderboard_overlay)
      // the canvas-space gamepadFocus rects can't represent the layout.
      // Translate dpad / A / B into synthetic keyboard events instead —
      // browser natively handles Tab/Shift+Tab between focusable
      // elements + Arrow keys on range sliders + Enter to click buttons.
      // Every button + slider + toggle in the panel is already focusable
      // by default DOM rules so this Just Works without per-element refs.
      const _isDomOverlay =
        activeScreen === 'settings_overlay' ||
        activeScreen === 'quests_overlay' ||
        activeScreen === 'leaderboard_overlay' ||
        activeScreen === 'auth_overlay';   // BETA 22.74 — sign-in overlay (B closes; typing still needs keyboard)
      if (_isDomOverlay) {
        // BETA 18.17 — Per Dylan: "start menu works but when i click
        // settings i cant move the hit box around at all in settings."
        // Root cause: synthetic KeyboardEvent('keydown', { key: 'Tab' })
        // fires listener callbacks but does NOT trigger native browser
        // focus cycling. Synthetic events can't drive native behaviors —
        // that's a Web API restriction. So the previous code that
        // dispatched synthetic Tab events looked correct but never
        // actually moved focus between settings buttons/sliders.
        // Fix: directly query focusable DOM elements + call .focus().
        const _focusableSel = 'button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])';
        const _getFocusables = (): HTMLElement[] => {
          if (typeof document === 'undefined') return [];
          const els = Array.from(document.querySelectorAll<HTMLElement>(_focusableSel));
          return els.filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null);
        };
        const _focusAdvance = (forward: boolean): void => {
          const list = _getFocusables();
          if (list.length === 0) return;
          const cur = document.activeElement as HTMLElement | null;
          let idx = cur ? list.indexOf(cur) : -1;
          if (idx < 0) idx = forward ? -1 : 0;
          const next = forward
            ? list[(idx + 1) % list.length]
            : list[(idx - 1 + list.length) % list.length];
          try { next?.focus(); } catch { /* */ }
        };
        const dispatchKey = (key: string): void => {
          // For non-Tab keys (ArrowLeft/Right/Enter/Escape) keep the
          // synthetic dispatch — those DO drive the focused element's
          // event listeners (range slider arrow keys work this way, etc.)
          if (typeof window !== 'undefined') {
            const target = (document.activeElement ?? window) as EventTarget;
            target.dispatchEvent(new KeyboardEvent('keydown', { key, bubbles: true }));
          }
        };
        const justPressed = (i: number) => isPressed(i) && !wasPressed(i);
        // Dpad up / Stick up → focus previous element
        // Dpad down / Stick down → focus next element
        // Dpad left → ArrowLeft (slider decrement on focused element)
        // Dpad right → ArrowRight (slider increment)
        // A button (0) → click() focused element OR Enter on it
        // B button (1) → Escape (closes panel via onBack listener)
        if (justPressed(12)) _focusAdvance(false);
        if (justPressed(13)) _focusAdvance(true);
        if (justPressed(14)) dispatchKey('ArrowLeft');
        if (justPressed(15)) dispatchKey('ArrowRight');
        if (justPressed(0)) {
          // Click the focused element directly — most reliable activator.
          const cur = document.activeElement as HTMLElement | null;
          if (cur && typeof cur.click === 'function') {
            try { cur.click(); } catch { /* */ }
          } else {
            dispatchKey('Enter');
          }
        }
        if (justPressed(1)) {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
          }
        }
        // Stick (axes 0/1) — fire focus advance on first crossing of threshold
        const sx = pad.axes[0] ?? 0, sy = pad.axes[1] ?? 0;
        const TH = 0.65;
        const _prevAxis = _menuEdge.get(99) === true;
        const _curAxisDown = sy > TH;
        const _curAxisUp = sy < -TH;
        if (_curAxisDown && !_prevAxis) _focusAdvance(true);
        if (_curAxisUp && !_prevAxis) _focusAdvance(false);
        _menuEdge.set(99, _curAxisDown || _curAxisUp);
        const _prevAxisH = _menuEdge.get(98) === true;
        const _curAxisR = sx > TH;
        const _curAxisL = sx < -TH;
        if (_curAxisR && !_prevAxisH) dispatchKey('ArrowRight');
        if (_curAxisL && !_prevAxisH) dispatchKey('ArrowLeft');
        _menuEdge.set(98, _curAxisR || _curAxisL);
        for (let i = 0; i < pad.buttons.length; i++) {
          _menuEdge.set(i, isPressed(i));
        }
        return;
      }
      // BETA 18.12 — Audit fix: do NOT call tickGamepadFocus from the
      // menu ticker for the canvas-side in-game choice screens
      // (levelup_cards, shrine_cards, chest_cards, gameover_screen,
      // won_screen). Those screens are already polled by the in-game
      // tickGamepad (called from updateGame every frame), which has
      // its own edge map AND drives the shared _navCooldown. When BOTH
      // tickers fire, they double-decrement _navCooldown, halving the
      // effective nav delay and causing "skips around and unpredictable"
      // D-pad behavior Dylan reported. Now the menu ticker only handles
      // screens that the in-game ticker can't reach: the Sorry Boss start
      // menu, pause_menu, and any future React-overlay
      // screens that aren't already handled by the DOM-overlay branch
      // above. Keep snapshotting edge for next frame regardless.
      // BETA 20.64 — UpdateBanner REFRESH via SELECT (button 8), MENU side
      // only. The banner is a DOM toast outside the focus system — controller
      // players could never press it (a real cause of Dylan's stale-build
      // sessions). In-game SELECT stays the pause toggle (line ~308), and a
      // forced reload mid-run would eat the player's run — so menus only.
      if (isPressed(8) && !wasPressed(8)) {
        const _ubr = (window as unknown as { __updateBannerRefresh?: () => void }).__updateBannerRefresh;
        if (_ubr) _ubr();
      }
      const _inGameCanvasScreen =
        activeScreen === 'levelup_cards' ||
        activeScreen === 'shrine_cards' ||
        activeScreen === 'chest_cards' ||
        activeScreen === 'gameover_screen' ||
        activeScreen === 'won_screen' ||
        // BETA 18.14 — Dylan: "i pause and i can not move the white hit box
        // anywhere in the pause menu." Same dual-decrement bug as the
        // canvas screens above: in-game tickGamepad runs every frame
        // (because updateGame keeps running during pause; the early-return
        // at line 1714 is AFTER tickGamepad at line 1335). When pause_menu
        // is active, BOTH tickers fire tickGamepadFocus, draining
        // _navCooldown 2x. Add pause_menu to the menu-ticker exclusion
        // list so only the in-game ticker handles pause input.
        activeScreen === 'pause_menu' ||
        // BETA 20.64 — ultra_chest is a canvas in-game pick screen too (was
        // keyboard-only before; now it registers focus targets like the rest).
        activeScreen === 'ultra_chest';
      if (!_inGameCanvasScreen) {
        tickGamepadFocus(pad, 1 / 30, { wasPressed, isPressed });
      }
      for (let i = 0; i < pad.buttons.length; i++) {
        _menuEdge.set(i, isPressed(i));
      }
    } catch { /* never crash the ticker */ }
  }, 33);
}
